import { useCallback, useEffect, useRef, useState } from "react";
import { Calculator, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@primitives/button";
import { Loader } from "@primitives/loader";

import { BFN_URL } from "@/config/bfnConfig";
import { BFN_IDLE_UNMOUNT_MS } from "@/config/bfnKeepAlive";
import { useBfnHistorySentinel } from "@/components/chat/bfn/useBfnHistorySentinel";
import { useBfnCalculatorState } from "@/zustand/BfnCalculatorState";
import { useStartSubscriptionView } from "@/hooks/useStartSubscriptionView";
import {
  BfnAccessDeniedError,
  establishBfnSession,
  exchangeBfnToken,
} from "@/server/services/bfnService";

type HandshakeStatus = "loading" | "ready" | "upsell" | "error";

// The calculator is a ~6.3 MB single-page app. If its `load` event still
// hasn't fired after this long, the boot overlay adds a subtle hint — it
// never blocks anything (the header close button works the whole time).
const FRAME_SLOW_LOAD_MS = 15_000;

type BfnCalculatorViewProps = {
  /** Overridable in tests so the slow-load hint doesn't need fake timers. */
  slowLoadMs?: number;
  /**
   * Keep-alive: true while the view stays mounted but not shown (ChatPage
   * passes `!isOpen`). Hidden means display:none — the browser suspends the
   * iframe's rendering/rAF entirely — plus an idle timer that fully unmounts
   * after BFN_IDLE_UNMOUNT_MS so the memory is never held indefinitely.
   */
  hidden?: boolean;
  /** Overridable in tests so idle unmount doesn't need fake timers. */
  idleUnmountMs?: number;
};

/**
 * Full-height overlay hosting the BFN Calculator in an iframe.
 *
 * Deliberately NOT a left-column list view: the calculator wants an edge-to-edge
 * surface, so this renders as a fixed overlay above the three chat columns
 * (mounted by ChatPage only when the flag is on AND the view is open).
 *
 * On open it performs the session handshake: (1) exchange the OA token for a
 * short-lived BFN token, (2) POST it to the gateway /session to set the session
 * cookie, (3) render the iframe (the cookie then rides its requests). A 403 at
 * step 1 means the user isn't entitled → upsell state, not an error. The
 * gateway signals cookie expiry via a `bfn:session-expired` postMessage, which
 * re-runs the handshake (strict origin check).
 */
export default function BfnCalculatorView({
  slowLoadMs = FRAME_SLOW_LOAD_MS,
  hidden = false,
  idleUnmountMs = BFN_IDLE_UNMOUNT_MS,
}: BfnCalculatorViewProps = {}) {
  const { t } = useTranslation();
  const close = useBfnCalculatorState((state) => state.close);
  const unmount = useBfnCalculatorState((state) => state.unmount);
  const startSubscriptionView = useStartSubscriptionView();
  const [status, setStatus] = useState<HandshakeStatus>("loading");
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [frameSlow, setFrameSlow] = useState(false);

  const runHandshake = useCallback(async () => {
    setStatus("loading");
    setFrameLoaded(false);
    setFrameSlow(false);
    try {
      const { token } = await exchangeBfnToken();
      await establishBfnSession(token);
      setStatus("ready");
    } catch (error) {
      setStatus(error instanceof BfnAccessDeniedError ? "upsell" : "error");
    }
  }, []);

  // First handshake starts on the first VISIBLE render, once. The ref (not a
  // hidden-keyed effect alone) is what stops a later re-show from
  // re-handshaking a warm calculator. Mounting hidden happens only with
  // stale keep-alive state after a route away from ChatPage — skip the
  // handshake there (nobody would see it) and let the non-ready effect below
  // drop the view instead of wasting two gateway calls per remount.
  const handshakeStarted = useRef(false);
  useEffect(() => {
    if (hidden || handshakeStarted.current) {
      return;
    }
    handshakeStarted.current = true;
    runHandshake();
  }, [hidden, runHandshake]);

  // Re-run the handshake when the gateway signals its session cookie expired.
  // Strict origin check: only messages from the configured BFN origin are
  // trusted; anything else is ignored.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== BFN_URL) {
        return;
      }
      if (
        typeof event.data === "object" &&
        event.data !== null &&
        event.data.type === "bfn:session-expired"
      ) {
        runHandshake();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [runHandshake]);

  // Back-button integration lives in useBfnHistorySentinel (sentinel push on
  // show, strip-in-place on hide, X consumes the entry). Visibility-keyed for
  // keep-alive — see the hook's doc comment for the router-safety rules.
  const requestClose = useBfnHistorySentinel(!hidden, close);

  // Keep-alive is only worth paying for a READY calculator. Closing from
  // upsell/error/loading unmounts instead, so the next open re-runs the
  // handshake — otherwise a user who upgrades via the upsell CTA would reopen
  // onto a STALE upsell screen (entitlement is checked by the handshake).
  useEffect(() => {
    if (hidden && status !== "ready") {
      unmount();
    }
  }, [hidden, status, unmount]);

  // Idle unmount: a hidden calculator holds renderer memory (~8 MB JS heap +
  // the 5.5 MB document's DOM), fine for a quick hop back but not worth
  // holding for a session spent elsewhere. After idleUnmountMs hidden, drop
  // it — the next open is simply a cold open again.
  useEffect(() => {
    if (!hidden) {
      return;
    }
    const timer = window.setTimeout(() => {
      // Re-check at fire time: at the exact idle boundary the queued callback
      // can outrace this effect's cleanup by one task (wider after a
      // background tab's throttled timers flush on refocus) — and unmount()
      // flips isOpen too, which would instantly close a just-reopened
      // calculator (dual-review finding).
      if (!useBfnCalculatorState.getState().isOpen) {
        unmount();
      }
    }, idleUnmountMs);
    return () => window.clearTimeout(timer);
  }, [hidden, unmount, idleUnmountMs]);

  // Upsell CTA: close the calculator overlay, then route to the in-app
  // subscription plans view so the user can upgrade.
  //
  // close() (not history.back()) is the deliberate close path here: flipping
  // isOpen unmounts the view via ChatPage's gate, and the unmount cleanup
  // strips the history sentinel IN PLACE (replaceState) — no traversal that
  // would race the navigation below. We then route through the shared
  // useStartSubscriptionView hook, which opens the plans view AND navigates to
  // "/" so the chatId is cleared from the URL: startSubscriptionView() nulls
  // activeChatId but never touches the URL, and a stale chatId there desyncs
  // Zustand from the router (a later click on that chat becomes a no-op). We
  // pass replace:true so the leftover sentinel entry is consumed rather than
  // left on the stack. See useStartSubscriptionView's doc comment.
  const viewPlans = useCallback(() => {
    close();
    startSubscriptionView({ replace: true });
  }, [close, startSubscriptionView]);

  // Surface the slow-load hint if the iframe never fires `load`.
  useEffect(() => {
    if (status !== "ready" || frameLoaded) {
      return;
    }
    const timer = window.setTimeout(() => setFrameSlow(true), slowLoadMs);
    return () => window.clearTimeout(timer);
  }, [status, frameLoaded, slowLoadMs]);

  return (
    // `hidden` swaps the display class entirely (never both): display:none
    // suspends the iframe's rendering work while keep-alive holds it warm.
    <div
      data-testid="bfn-calculator-view"
      className={`fixed inset-0 z-[70] flex-col bg-background pt-[env(safe-area-inset-top,0px)] ${
        hidden ? "hidden" : "flex"
      }`}
    >
      {/* No title header — the calculator ships its own (POLYMATH) header, so a
          second one stacked on top looks wrong. We keep only a floating close
          button, present in every state so the user can always exit. The
          semi-transparent backdrop keeps the X legible over any calculator tab
          (light or dark). */}
      <Button
        onClick={requestClose}
        size="icon"
        variant="ghost"
        aria-label={t("bfn.close")}
        className="absolute right-2 top-[calc(0.5rem+env(safe-area-inset-top,0px))] z-20 rounded-full bg-background/70 backdrop-blur-sm hover:bg-background/90"
      >
        <X size={22} strokeWidth={1.5} />
      </Button>

      <div className="relative flex-1 overflow-hidden">
        {status === "loading" && (
          <div className="flex size-full items-center justify-center">
            <Loader2 className="size-8 animate-spin text-secondary" />
          </div>
        )}

        {status === "upsell" && (
          <div className="mx-auto flex size-full max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
            <Calculator size={40} strokeWidth={1.5} className="text-primary" />
            <h2 className="text-lg font-semibold">{t("bfn.upsellTitle")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("bfn.upsellBody")}
            </p>
            <Button className="mt-2" onClick={viewPlans}>
              {t("bfn.upsellCta")}
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="mx-auto flex size-full max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm text-muted-foreground">
              {t("bfn.errorBody")}
            </p>
            <Button variant="secondary" onClick={() => runHandshake()}>
              {t("bfn.retry")}
            </Button>
          </div>
        )}

        {status === "ready" && (
          <>
            <iframe
              src={BFN_URL}
              title={t("bfn.title")}
              className="size-full border-0"
              onLoad={() => setFrameLoaded(true)}
              // allow-same-origin is REQUIRED so the gateway's __Host- session
              // cookie is scoped to the frame's real origin; scripts + forms are
              // needed by the calculator. Omitting allow-top-navigation/popups
              // blocks the framed app from navigating the parent or opening popups.
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
            {/* Boot overlay: the calculator bundle is heavy, so cover the blank
                iframe with a spinner until its `load` event fires. Overlaid on
                top (the iframe mounts and downloads underneath), and
                pointer-events-none so it can never trap input. */}
            {!frameLoaded && (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background">
                <Loader size={32} className="text-secondary" />
                {frameSlow && (
                  <p className="max-w-md px-6 text-center text-sm text-muted-foreground">
                    {t("bfn.slowLoad")}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
